import { ArrowLeft } from 'lucide-react';
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { detectSubdomain } from '@/lib/subdomain';
import { Button } from '@/components/ui/button';
import { SeoHead } from '@/components/seo-head';

export function PrivacyPolicyPage() {
  // Render the example subdomain contextually · show only the one the user
  // is currently on. When accessed from the apex (no subdomain), drop the
  // example entirely instead of listing every partner's URL.
  const subdomain = useMemo(() => detectSubdomain(), []);

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <SeoHead
        title="Política de Privacidade · NexoFácil"
        description="Como a NexoFácil e seus parceiros tratam dados pessoais de leads em conformidade com a LGPD."
      />
      <header className="border-b bg-background">
        <div className="container flex h-16 items-center gap-3">
          <Button asChild variant="ghost" size="sm" className="-ml-2">
            <Link to="/">
              <ArrowLeft className="mr-1.5 h-4 w-4" />
              Voltar
            </Link>
          </Button>
          <div className="ml-auto flex items-center gap-2">
            <img src="/nexofacil-icon.png" alt="" className="h-6 w-6" />
            <span className="text-sm font-semibold">NexoFácil</span>
          </div>
        </div>
      </header>

      <main className="flex-1">
        <article className="container max-w-3xl py-12 md:py-16">
          <h1 className="text-3xl font-extrabold tracking-tight md:text-4xl">
            Política de Privacidade
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Última atualização: 1º de junho de 2026
          </p>

          <div className="prose prose-slate mt-8 max-w-none space-y-6 text-[15px] leading-relaxed text-foreground">
            <Section title="1. Quem somos">
              <p>
                A <strong>NexoFácil</strong> opera esta plataforma de
                qualificação para parceiros em diferentes segmentos:
                financiamento imobiliário Minha Casa Minha Vida (MCMV),
                proteção veicular, e outros produtos conforme expandimos.
                {subdomain ? (
                  <>
                    {' '}Esta página é a do parceiro{' '}
                    <code>{subdomain}.nexofacil.com.br</code>.
                  </>
                ) : null}{' '}
                Quando você preenche o formulário de qualificação ou cotação,
                seus dados são tratados em conjunto por{' '}
                <strong>NexoFácil</strong> (responsável pela plataforma) e
                pelo <strong>parceiro</strong> indicado no subdomínio
                (responsável pelo atendimento comercial).
              </p>
            </Section>

            <Section title="2. Quais dados coletamos">
              <p>
                Os dados específicos coletados variam conforme o produto que
                o corretor oferece. Em todos os casos:
              </p>
              <ul className="list-disc space-y-2 pl-6">
                <li>
                  <strong>Identificação e contato:</strong> nome, e-mail, número
                  de WhatsApp.
                </li>
                <li>
                  <strong>Origem da visita:</strong> parâmetros UTM, fbclid,
                  página de entrada e referenciador, IP e user-agent · usados
                  para medir a eficácia das campanhas de divulgação.
                </li>
                <li>
                  <strong>Cookies:</strong> identificadores de campanha do
                  Meta (<code>_fbp</code>, <code>_fbc</code>) usados em
                  combinação com a Conversions API descrita abaixo.
                </li>
              </ul>
              <p>
                <strong>Em formulários MCMV (imobiliário):</strong> faixa de
                renda mensal familiar, fonte de renda, idade, estado civil,
                dependentes, situação de FGTS, se possui imóvel próprio, se
                já usou o MCMV, situação no SPC/Serasa e disponibilidade de
                entrada · todos autodeclarados.
              </p>
              <p>
                <strong>Em formulários de proteção veicular:</strong> tipo,
                ano e valor aproximado do veículo (faixa FIPE); quilometragem
                mensal; local de pernoite; cidade/estado de uso; idade do
                titular; histórico de sinistros no último ano; plano
                desejado.
              </p>
              <p className="text-sm text-muted-foreground">
                Não realizamos consulta automatizada ao SPC, Serasa, bureaus
                de crédito, base do Detran ou de seguradoras. As respostas são
                autodeclaradas e usadas apenas para uma estimativa informativa
                do seu enquadramento no produto consultado.
              </p>
            </Section>

            <Section title="3. Para que usamos seus dados">
              <ul className="list-disc space-y-2 pl-6">
                <li>
                  Calcular sua estimativa de enquadramento no produto
                  consultado (financiamento MCMV, plano de proteção veicular,
                  etc.) e mostrar o resultado.
                </li>
                <li>
                  Permitir que o corretor/parceiro entre em contato pelo
                  WhatsApp para continuar o atendimento.
                </li>
                <li>
                  Enviar eventos agregados e com identificadores hasheados
                  para a Meta Conversions API, para otimizar nossas campanhas
                  e mostrar anúncios mais relevantes.
                </li>
                <li>
                  Acompanhar o funil comercial (lead recebido, atendido,
                  proposta, venda) para melhorar o serviço.
                </li>
              </ul>
            </Section>

            <Section title="4. Bases legais (LGPD)">
              <p>
                Tratamos seus dados com base nas seguintes hipóteses do art. 7º
                da Lei nº 13.709/2018:
              </p>
              <ul className="list-disc space-y-2 pl-6">
                <li>
                  <strong>Consentimento</strong> · você fornece os dados
                  voluntariamente ao enviar o formulário, ciente de que serão
                  encaminhados ao corretor.
                </li>
                <li>
                  <strong>Execução de procedimentos preliminares a contrato</strong>
                  {' '}· os dados permitem ao corretor avaliar e propor a
                  contratação do financiamento que você buscou.
                </li>
                <li>
                  <strong>Legítimo interesse</strong> · para medir e otimizar
                  a divulgação da plataforma, sempre com identificadores
                  pseudonimizados (hash SHA-256).
                </li>
              </ul>
            </Section>

            <Section title="5. Com quem compartilhamos">
              <ul className="list-disc space-y-2 pl-6">
                <li>
                  <strong>Corretor responsável pelo subdomínio acessado</strong>{' '}
                  · recebe os dados completos do formulário para fazer o
                  atendimento.
                </li>
                <li>
                  <strong>Meta Platforms, Inc.</strong> (Facebook/Instagram) ·
                  recebe, via Conversions API, identificadores hasheados
                  (e-mail, telefone, nome) e eventos de conversão. O Meta
                  usa esses dados para mensurar e otimizar anúncios. Mais em{' '}
                  <a
                    href="https://www.facebook.com/privacy/policy/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary underline"
                  >
                    política de privacidade do Meta
                  </a>
                  .
                </li>
                <li>
                  <strong>Provedores de infraestrutura</strong> (AWS, MongoDB
                  Atlas, Firebase) · hospedam os dados; tratam apenas como
                  operadores, sem acesso comercial às informações.
                </li>
              </ul>
              <p>
                Não vendemos seus dados, não os compartilhamos com bancos
                automaticamente, nem os disponibilizamos a terceiros não
                listados aqui.
              </p>
            </Section>

            <Section title="6. Por quanto tempo guardamos">
              <p>
                Mantemos seus dados enquanto durar o relacionamento comercial
                com o corretor ou pelo prazo necessário para cumprir obrigações
                legais (até 5 anos para fins fiscais e regulatórios após o
                último contato). Após esse prazo, são anonimizados ou
                eliminados.
              </p>
            </Section>

            <Section title="7. Seus direitos">
              <p>
                Conforme o art. 18 da LGPD, você pode a qualquer momento:
              </p>
              <ul className="list-disc space-y-2 pl-6">
                <li>Confirmar a existência de tratamento dos seus dados;</li>
                <li>Acessar os dados que temos sobre você;</li>
                <li>Corrigir dados incompletos, inexatos ou desatualizados;</li>
                <li>
                  Solicitar anonimização, bloqueio ou eliminação de dados
                  desnecessários;
                </li>
                <li>
                  Revogar o consentimento e solicitar exclusão dos dados
                  tratados com essa base;
                </li>
                <li>Pedir informações sobre com quem compartilhamos os dados;</li>
                <li>Opor-se a tratamento feito com base em legítimo interesse.</li>
              </ul>
            </Section>

            <Section title="8. Como exercer seus direitos">
              <p>
                Envie um e-mail para{' '}
                <a
                  href="mailto:contato@nexofacil.com.br"
                  className="text-primary underline"
                >
                  contato@nexofacil.com.br
                </a>{' '}
                informando o seu nome completo, e-mail usado no formulário e
                o pedido específico (acesso, correção, exclusão etc).
                Respondemos em até 15 dias.
              </p>
            </Section>

            <Section title="9. Segurança">
              <p>
                Hospedamos os dados em infraestrutura na AWS (região
                us-east-1), com tráfego criptografado em TLS, senhas no
                Firebase Authentication e segredos gerenciados pelo AWS
                Systems Manager. Identificadores pessoais enviados ao Meta são
                hasheados em SHA-256 antes da transmissão.
              </p>
            </Section>

            <Section title="10. Cookies e tecnologias similares">
              <p>
                Usamos apenas cookies estritamente necessários para o
                funcionamento da plataforma e identificadores do Meta Pixel
                (<code>_fbp</code>, <code>_fbc</code>) para mensurar
                conversões publicitárias. Você pode bloquear cookies no seu
                navegador a qualquer momento · algumas funcionalidades podem
                ficar prejudicadas.
              </p>
            </Section>

            <Section title="11. Alterações desta política">
              <p>
                Podemos atualizar este documento a qualquer momento. A data
                no topo indica a versão mais recente. Mudanças substanciais
                serão comunicadas no site.
              </p>
            </Section>
          </div>
        </article>
      </main>

      <footer className="border-t bg-background">
        <div className="container py-6 text-center text-xs text-muted-foreground">
          NexoFácil · {new Date().getFullYear()}
        </div>
      </footer>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-3">
      <h2 className="text-xl font-bold tracking-tight text-foreground">{title}</h2>
      {children}
    </section>
  );
}
